import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Patient } from "lib/build/patients";
import { Schema } from "lib/build/schema";

import { Button } from "src/common/components/Button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import { Checkbox } from "src/common/components/Checkbox";
import { Field } from "src/common/components/Field";
import {
	Fieldset,
	Form,
	FormBody,
	FormFooter
} from "src/common/components/Form";
import {
	GridColumn,
	GridRow,
	SplitColumn,
	SplitRow
} from "src/common/components/Grid";
import { FormPage } from "src/common/components/Page";
import { api, isAxiosError } from "src/common/utils/api";
import { useAuthState } from "src/features/auth";
import {
	FieldData,
	FieldsetData,
	FieldsetRenderer,
	FormContextProvider,
	useFormState
} from "src/features/forms";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { genericMessages } from "src/messages/generic";
import { AddPatient } from "src/views/patient-form/AddPatient";
import { DataHandlingMessage } from "src/views/patient-form/DataHandlingMessage";
import { messages as sharedMessages } from "src/views/patient-form/messages";

const validator = Schema.form().properties({
	socialSecurityNumber: Schema.socialSecurityNumber()
});

const messages = defineMessages({
	title: {
		id: "form.add_or_edit_patient",
		defaultMessage: "Páciens felvétele"
	}
});

const errorMessages = {
	socialSecurityNumber: {
		presence: sharedMessages.socialSecurityNumberRequired,
		format: sharedMessages.socialSecurityNumberFormat
	}
};

const fields: {
	[key: string]: FieldData;
} = {
	socialSecurityNumber: {
		type: "field",
		path: ["socialSecurityNumber"],
		label: sharedMessages.socialSecurityNumber,
		errorMessages: errorMessages.socialSecurityNumber,
		input: { type: "input", props: { type: "social-security-number" } }
	}
};

const fieldset: FieldsetData = {
	rows: [{ columns: [{ width: 12, field: fields.socialSecurityNumber }] }]
};

export const AddOrEditPatient: React.FunctionComponent = () => {
	const { org, mod } = useOrgModParams();
	const { user } = useAuthState();

	const formState = useFormState({ validator });

	const formStateRef = React.useRef(formState);
	React.useEffect(() => {
		formStateRef.current = formState;
	}, [formState]);

	const [hasAcceptedDataHandling, onChangeAcceptDataHandling] =
		React.useState(false);

	const navigate = useNavigate();
	const createGenericError = useCreateGenericError();

	const [{ isSubmitted, isLoading, renderAddPatient }, setState] =
		React.useState({
			isSubmitted: false,
			isLoading: false,
			renderAddPatient: false
		});

	const forward = React.useCallback(
		async (userId: string, ssn: string) => {
			try {
				const {
					data: { id: patientId, connectedUsers }
				} = await api.get<Patient>(
					`/ssn/${ssn}/detailed?organization=${org}&module=${mod}`
				);
				if (!connectedUsers || !connectedUsers.includes(userId)) {
					await api.put(
						`/patients/connections/${patientId}?organization=${org}&module=${mod}`,
						[...connectedUsers, userId]
					);
				}
				navigate(`/${org}/${mod}/patients/${patientId}/edit`);
			} catch (error) {
				if (
					isAxiosError(error) &&
					error.response !== undefined &&
					error.response.status === 404
				) {
					setState(state => ({ ...state, renderAddPatient: true }));
				} else {
					createGenericError();
				}
			}
		},
		[org, mod, navigate, createGenericError]
	);

	const onSubmit = React.useCallback(() => {
		const { current: formState } = formStateRef;
		setState(state => ({ ...state, isSubmitted: true }));
		if (user !== null && formState.errors.length === 0) {
			const { current: formState } = formStateRef;
			const {
				value: { socialSecurityNumber: ssn }
			} = formState;
			forward(user.id, ssn);
		}
	}, [forward, user]);

	if (renderAddPatient) {
		return (
			<AddPatient
				socialSecurityNumber={formState.value.socialSecurityNumber}
			/>
		);
	}

	return (
		<FormPage variant="ultra-narrow">
			<Card>
				<CardHeader>
					<CardTitle>
						<FormattedMessage {...messages.title} />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<FormContextProvider {...formState}>
						<Form isSubmitted={isSubmitted} onSubmit={onSubmit}>
							<FormBody>
								<Fieldset>
									<FieldsetRenderer {...fieldset} />
								</Fieldset>
								<Fieldset>
									<GridRow>
										<GridColumn columnSize={12}>
											<Field>
												<Checkbox
													checked={
														hasAcceptedDataHandling
													}
													onChangeChecked={
														onChangeAcceptDataHandling
													}
												>
													<DataHandlingMessage />
												</Checkbox>
											</Field>
										</GridColumn>
									</GridRow>
								</Fieldset>
							</FormBody>
							<FormFooter>
								<SplitRow>
									<SplitColumn side="right">
										<Button
											type="submit"
											disabled={!hasAcceptedDataHandling}
											isLoading={isLoading}
										>
											<FormattedMessage
												{...genericMessages.forward}
											/>
										</Button>
									</SplitColumn>
								</SplitRow>
							</FormFooter>
						</Form>
					</FormContextProvider>
				</CardContent>
			</Card>
		</FormPage>
	);
};
